import { createContext, useContext, useState, useEffect, useRef, useCallback, startTransition, ReactNode } from "react";
import { modelsTier, orderModels } from "@/lib/aiModels";

// Legacy type for backwards compatibility
export type PreviewPlanType = "free" | "paid_oneoff" | "subscription_active" | "subscription_expired" | "no_plan" | "free_trial";

// Subscription status types
export type PreviewSubscriptionStatus = "active" | "expired" | "trial" | "none";

// Payment mode types  
export type PreviewPaymentMode = "subscription" | "one_off" | "free";

export type PreviewUserType = "agency" | "client";

export interface PreviewPlanTier {
  id: string;
  slug: string;
  name: string;
  name_el: string | null;
  pages_per_month: number;
  pages_per_analysis: number;
  free_audits_limit: number;
  credits_per_month: number;  // Use this instead of pages_per_month * 10
  // Full plan limits for accurate preview behavior
  max_projects: number;
  max_clients: number;
  max_team_members: number;
  // Pricing fields for subscription display
  base_price_monthly: number;
  base_price_yearly: number;
  display_order: number;
}

interface AdminPreviewSettings {
  enabled: boolean;
  // New structured approach
  paymentMode: PreviewPaymentMode;
  subscriptionStatus: PreviewSubscriptionStatus;
  selectedPlanTierId: string | null;
  selectedPlanTierSlug: string | null;
  selectedPlanTierData: PreviewPlanTier | null;
  // One-off credits simulation
  simulatedOneOffCredits: number;
  simulatedBonusCredits: number;
  // Simulated credit usage (for tracking consumption during preview)
  simulatedCreditsUsed: number;
  // Existing
  userType: PreviewUserType;
  /**
   * Models chosen for the next analyses (src/lib/aiModels.ts), strongest
   * first. Empty means the plan default. Sent only while Preview is on, and
   * the server honours it for admins only.
   */
  aiModels: string[];
}

interface AdminPreviewContextType {
  settings: AdminPreviewSettings;
  updateSettings: (updates: Partial<AdminPreviewSettings>) => void;
  resetSettings: () => void;
  deductPreviewCredits: (amount: number) => boolean;
  resetPreviewUsage: () => void;
  isAdmin: boolean;
  isAdminChecking: boolean;
  adminCheckError: string | null;
  refreshAdminStatus: () => Promise<void>;
}

const defaultSettings: AdminPreviewSettings = {
  enabled: false,
  paymentMode: "subscription",
  subscriptionStatus: "active",
  selectedPlanTierId: null,
  selectedPlanTierSlug: "starter",
  selectedPlanTierData: null,
  simulatedOneOffCredits: 0,
  simulatedBonusCredits: 0,
  simulatedCreditsUsed: 0,
  userType: "agency",
  aiModels: [],
};

const STORAGE_KEY = "admin_preview_settings";

const getSupabase = () => import("@/integrations/supabase/client").then(m => m.supabase);

/** The verify-admin call for the signed-in user, shared by every check in this page load. */
let adminCheck: { userId: string; result: Promise<{ data: { isAdmin?: boolean } | null; error: { message?: string } | null }> } | null = null;

const AdminPreviewContext = createContext<AdminPreviewContextType | undefined>(undefined);

export function AdminPreviewProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AdminPreviewSettings>(() => {
    // Build-time prerender (scripts/prerender.mjs) has no storage.
    if (typeof window === "undefined") return defaultSettings;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const rest = { ...(JSON.parse(stored) ?? {}) };
        // Settings saved before the model picker carried aiModel/geminiProEnabled.
        delete rest.aiModel;
        delete rest.geminiProEnabled;
        return { ...defaultSettings, ...rest, aiModels: orderModels(Array.isArray(rest.aiModels) ? rest.aiModels : []) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Ασφαλής προεπιλογή: ξεκινάμε με isAdmin=false και το ενεργοποιούμε ΜΟΝΟ μετά από επιτυχή επαλήθευση.
  const [isAdmin, setIsAdminNow] = useState(false);
  const [isAdminChecking, setIsAdminCheckingNow] = useState(false);
  const [adminCheckError, setAdminCheckErrorNow] = useState<string | null>(null);

  // The check runs on every page load, also while a prerendered page is still
  // hydrating. As transitions, its updates wait for hydration to finish instead
  // of making React throw away the page's HTML and render it again.
  const setIsAdmin = (value: boolean) => startTransition(() => setIsAdminNow(value));
  const setIsAdminChecking = (value: boolean) => startTransition(() => setIsAdminCheckingNow(value));
  const setAdminCheckError = (value: string | null) => startTransition(() => setAdminCheckErrorNow(value));

  const checkAdmin = async () => {
    setIsAdminChecking(true);
    setAdminCheckError(null);

    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();

      // No session = not logged in
      if (!session) {
        adminCheck = null;
        setIsAdmin(false);
        setIsAdminChecking(false);
        return;
      }

      // One verification per signed-in user per page load. Auth events fire
      // several times on startup (initial session, token refresh), and each
      // used to call the edge function again (~400 ms each).
      if (!adminCheck || adminCheck.userId !== session.user.id) {
        adminCheck = { userId: session.user.id, result: supabase.functions.invoke("verify-admin") };
      }
      const { data, error } = await adminCheck.result;

      if (error) {
        adminCheck = null; // let the next check try again
        const msg = error.message ?? "Verification failed";

        // Αν δεν μπορούμε να επαληθεύσουμε admin (π.χ. 401), θεωρούμε τον χρήστη non-admin.
        if (msg.includes('401') || msg.toLowerCase().includes('unauthorized') || msg.toLowerCase().includes('non-2xx')) {
          setIsAdmin(false);
        }

        // Avoid noisy logs
        if (!msg.includes('401') && !msg.includes('non-2xx')) {
          console.warn("Admin verification issue:", msg);
        }

        setAdminCheckError(msg);
        setIsAdminChecking(false);
        return;
      }

      setIsAdmin(data?.isAdmin === true);
    } catch (err) {
      setAdminCheckError(err instanceof Error ? err.message : "Unknown error");
      // Fail-closed: αν κάτι πάει στραβά, μη δίνουμε admin/preview δυνατότητες.
      setIsAdmin(false);
    } finally {
      setIsAdminChecking(false);
    }
  };

  useEffect(() => {
    checkAdmin();

    let subscription: { unsubscribe: () => void } | null = null;
    getSupabase().then(supabase => {
      const { data } = supabase.auth.onAuthStateChange(() => {
        checkAdmin();
      });
      subscription = data.subscription;
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Use ref to track current plan tier ID and avoid dependency issues
  const planTierIdRef = useRef(settings.selectedPlanTierId);
  useEffect(() => {
    planTierIdRef.current = settings.selectedPlanTierId;
  }, [settings.selectedPlanTierId]);

  // Ensure plan tier data is always available and UP-TO-DATE when admin is active.
  // This makes Preview work even if the user never visits the Admin Preview page.
  // ALWAYS fetches fresh data from DB to ensure admin changes are reflected immediately.
  const refreshPlanTierData = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const supabase = await getSupabase();
      const currentPlanTierId = planTierIdRef.current;
      
      // If we have an id, ALWAYS refresh from DB to get latest values
      if (currentPlanTierId) {
        const { data, error } = await supabase
          .from("plan_tiers")
          .select("id, slug, name, name_el, pages_per_month, pages_per_analysis, free_audits_limit, credits_per_month, max_projects, max_clients, max_team_members, base_price_monthly, base_price_yearly, display_order")
          .eq("id", currentPlanTierId)
          .maybeSingle();

        if (!error && data) {
          console.log('[AdminPreview] Refreshing plan tier data:', data.slug, 'credits:', data.credits_per_month);
          setSettings(prev => ({
            ...prev,
            selectedPlanTierSlug: data.slug,
            selectedPlanTierData: data,
          }));
        }
        return;
      }

      // If no id, pick a sensible default (starter if present)
      const desiredSlug = settings.selectedPlanTierSlug || "starter";

      const { data, error } = await supabase
        .from("plan_tiers")
        .select("id, slug, name, name_el, pages_per_month, pages_per_analysis, free_audits_limit, credits_per_month, max_projects, max_clients, max_team_members, base_price_monthly, base_price_yearly, display_order")
        .eq("is_active", true)
        .order("display_order");

      if (error || !data || data.length === 0) return;

      const chosen = data.find((p) => p.slug === desiredSlug) || data[0];
      setSettings(prev => ({
        ...prev,
        selectedPlanTierId: chosen.id,
        selectedPlanTierSlug: chosen.slug,
        selectedPlanTierData: chosen,
      }));
    } catch {
      // fail silently; preview will fall back to defaults
    }
  }, [isAdmin]);

  // Initial fetch and realtime subscription
  useEffect(() => {
    if (!isAdmin) return;

    // Initial refresh
    refreshPlanTierData();

    // Subscribe to realtime changes on plan_tiers table
    let channel: any = null;
    getSupabase().then(supabase => {
      channel = supabase
        .channel('plan_tiers_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'plan_tiers',
          },
          () => {
            refreshPlanTierData();
          }
        )
        .subscribe();
    });

    return () => {
      if (channel) {
        getSupabase().then(supabase => supabase.removeChannel(channel));
      }
    };
  }, [isAdmin, refreshPlanTierData]);

  // Save to localStorage when settings change. Staff only: a visitor's device
  // gets nothing it does not need (the cookie banner's "necessary" promise).
  useEffect(() => {
    if (!isAdmin) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* storage blocked */ }
  }, [settings, isAdmin]);

  const updateSettings = (updates: Partial<AdminPreviewSettings>) => {
    setSettings(prev => {
      const newSettings = { ...prev, ...updates };
      if (updates.aiModels) newSettings.aiModels = orderModels(updates.aiModels);
      return newSettings;
    });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Deduct credits from simulated balance (returns true if successful)
  const deductPreviewCredits = (amount: number): boolean => {
    // Calculate total available credits - use credits_per_month directly
    const planCredits = settings.selectedPlanTierData?.credits_per_month || 0;
    const totalCredits = planCredits + settings.simulatedOneOffCredits + settings.simulatedBonusCredits;
    const remainingCredits = totalCredits - settings.simulatedCreditsUsed;

    if (remainingCredits < amount) {
      return false;
    }

    setSettings(prev => ({
      ...prev,
      simulatedCreditsUsed: prev.simulatedCreditsUsed + amount
    }));
    return true;
  };

  // Reset simulated usage (for testing fresh state)
  const resetPreviewUsage = () => {
    setSettings(prev => ({
      ...prev,
      simulatedCreditsUsed: 0
    }));
  };

  return (
    <AdminPreviewContext.Provider
      value={{
        settings,
        updateSettings,
        resetSettings,
        deductPreviewCredits,
        resetPreviewUsage,
        isAdmin,
        isAdminChecking,
        adminCheckError,
        refreshAdminStatus: () => { adminCheck = null; return checkAdmin(); },
      }}
    >
      {children}
    </AdminPreviewContext.Provider>
  );
}

export function useAdminPreview() {
  const context = useContext(AdminPreviewContext);
  if (context === undefined) {
    throw new Error("useAdminPreview must be used within an AdminPreviewProvider");
  }
  return context;
}

export function useEffectiveUserState() {
  const { settings, isAdmin, deductPreviewCredits, resetPreviewUsage } = useAdminPreview();

  // Preview mode is only enabled when the admin explicitly enables it
  // This allows admins to use their real subscription normally
  const previewEnabled = isAdmin && settings.enabled;

  // Derive legacy planType for backwards compatibility
  const derivedPlanType = (): string | null => {
    if (!previewEnabled) return null;
    
    if (settings.paymentMode === "free") return "free";
    if (settings.paymentMode === "one_off") return "paid_oneoff";
    if (settings.paymentMode === "subscription") {
      if (settings.subscriptionStatus === "expired") return "subscription_expired";
      if (settings.subscriptionStatus === "trial") return "free_trial";
      if (settings.subscriptionStatus === "none") return "no_plan";
      return "subscription_active";
    }
    return null;
  };

  // Calculate remaining credits for preview mode
  const getPreviewRemainingCredits = (): number => {
    if (!previewEnabled) return 0;
    const planCredits = settings.selectedPlanTierData?.credits_per_month || 0;
    const totalCredits = planCredits + settings.simulatedOneOffCredits + settings.simulatedBonusCredits;
    return Math.max(0, totalCredits - settings.simulatedCreditsUsed);
  };

  return {
    isPreviewActive: previewEnabled,
    effectivePlanType: derivedPlanType(),
    effectivePaymentMode: previewEnabled ? settings.paymentMode : null,
    effectiveSubscriptionStatus: previewEnabled ? settings.subscriptionStatus : null,
    effectivePlanTierId: previewEnabled ? settings.selectedPlanTierId : null,
    effectivePlanTierSlug: previewEnabled ? settings.selectedPlanTierSlug : null,
    effectivePlanTierData: previewEnabled ? settings.selectedPlanTierData : null,
    effectiveOneOffCredits: previewEnabled ? settings.simulatedOneOffCredits : 0,
    effectiveBonusCredits: previewEnabled ? settings.simulatedBonusCredits : 0,
    effectiveCreditsUsed: previewEnabled ? settings.simulatedCreditsUsed : 0,
    effectiveRemainingCredits: getPreviewRemainingCredits(),
    effectiveUserType: previewEnabled ? settings.userType : null,
    // Actions for preview mode
    deductPreviewCredits,
    resetPreviewUsage,
  };
}

/**
 * The model plan to send with a new analysis ("models:a+b"), or undefined
 * for the plan default. Only an admin with Preview mode on has one.
 */
export function useAdminModelTier(): string | undefined {
  const { settings, isAdmin } = useAdminPreview();
  return isAdmin && settings.enabled ? modelsTier(settings.aiModels) : undefined;
}
